<template>
  <div>
    <v-row no-gutters style="gap: 0.5rem" class="align-center">
      <v-col cols="auto" class="d-flex align-center">
        <span>Ocena stanu ochrony</span>
      </v-col>
      <v-col cols="auto">
        <base-rounded-button @click="openAddScoreDialog" icon="mdi-list-status" size="x-small" color="primary" />
      </v-col>
    </v-row>
    <v-row v-if="data.scoreParameters && data.scoreParameters.length" class="mt-4" no-gutters>
      <v-col cols="12" class="d-flex">
        <rzi-scores-list
          is-visible
          :structure="data"
          :item="item"
          :current-metadata="currentMetadata"
          :scores-structure="scoresStructure"
        />
      </v-col>
    </v-row>
    <main-large-dialog
      v-if="isAddScoreDialogLoaded"
      :is-visible.sync="isAddScoreDialogVisible"
      :title="$i18n.t('table.rziDocuments.makeScore')"
      agree-text="dialog.agreeSave"
      :is-valid="isDialogValid"
      :loading="isDialogLoading"
      dialog-width="60%"
      @agree="handleAddEditScores"
    >
      <template #body>
        <v-form v-model="isDialogValid" ref="scores-dialog" class="flex-grow-1">
          <rzi-scores-list
            ref="new-scores"
            :is-visible="isAddScoreDialogVisible"
            :structure="data"
            :item="item"
            is-editing-on
            :current-metadata="currentMetadata"
            :scores-structure="scoresStructure"
          />
        </v-form>
      </template>
    </main-large-dialog>
  </div>
</template>

<script>
import RziScoresList from '@/components/RziScoresList';
import MainLargeDialog from '@/components/MainLargeDialog';

import { call, get } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RziScores',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    RziScoresList,
    MainLargeDialog,
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    dataProxy: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isDialogLoading: false,
    data: null,
    isDialogValid: false,
    isAddScoreDialogLoaded: false,
    isAddScoreDialogVisible: false,
  }),
  computed: {
    scores: get('rziData/scores'),
    currentBindedAttribute() {
      const attributes = this.currentMetadata.score.indexes.attributes;
      return attributes.bindedParameter ?? attributes.bindedParent;
    },
    groupedRawIndexes() {
      return this.dataProxy.rawIndexes
        ? this.$_groupByProperty(
            this.dataProxy.rawIndexes,
            'properties',
            this.currentMetadata.score.dictRawIndexes.attributes.parameter
          )
        : {};
    },
    scoresStructure() {
      return this.dataProxy.rawParameters.reduce((acc, parameter) => {
        const matchedIndexes = this.groupedRawIndexes[parameter.id];
        const paramItem = {
          title: this.getAttributeTextValueByName(
            parameter.properties[this.currentMetadata.score.dictRawParameters.attributes.name],
            this.currentMetadata.score.dictRawParameters.attributes.name,
            this.currentMetadata.score.dictRawParameters.datasource
          ),
          feature: parameter,
          indexes: (matchedIndexes || []).map(index => ({
            title: this.getAttributeTextValueByName(
              index.properties[this.currentMetadata.score.dictRawIndexes.attributes.name],
              this.currentMetadata.score.dictRawIndexes.attributes.name,
              this.currentMetadata.score.dictRawIndexes.datasource
            ),
            feature: index,
          })),
        };
        acc = [...acc, paramItem];
        return acc;
      }, []);
    },
  },
  methods: {
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    getMultipleFeaturesEditPayload(type, data, insertedParameters) {
      return Object.values(data).reduce((acc, item) => {
        const indexesWithScores = !!this.currentMetadata.score.indexes.attributes.score;
        const itemDesc = item.properties[this.currentMetadata.score[type].attributes.description];
        const actionArray = item.id ? (!itemDesc && !indexesWithScores ? 'delete' : 'update') : 'insert';
        if (actionArray === 'delete') {
          if (!acc[actionArray]) acc[actionArray] = { features_ids: [] };
          acc[actionArray].features_ids.push(item.id);
        } else {
          if (!itemDesc && !indexesWithScores) return acc;
          if (!acc[actionArray]) acc[actionArray] = [];
          let bindedId;
          if (this.currentMetadata.type === 'Monitoring') {
            bindedId = item.rawParentId;
          } else {
            bindedId =
              (insertedParameters || []).find(
                insertedParam =>
                  insertedParam[this.currentMetadata.score.parameters.attributes.title] === item.rawParameterIdName
              )?.id ||
              this.dataProxy.scoreParameters.find(
                param =>
                  param.properties[this.currentMetadata.score.parameters.attributes.title] === item.rawParameterId
              )?.id;
          }
          acc[actionArray].push(
            actionArray === 'update'
              ? {
                  fid: item.id,
                  properties: item.properties,
                }
              : {
                  ...item.properties,
                  [this.currentBindedAttribute]: item.properties[this.currentBindedAttribute] || bindedId,
                }
          );
        }
        return acc;
      }, {});
    },
    async handleAddEditScores() {
      this.isDialogLoading = true;
      try {
        const scoresData = this.$refs['new-scores'].data;
        const addEditParametersPayload = this.getMultipleFeaturesEditPayload('parameters', scoresData.parameters);
        if (this.currentMetadata.type === 'Monitoring') {
          await Promise.all([
            this.editMultipleDataSourceFeatures({
              ...addEditParametersPayload,
              data_source_name: this.currentMetadata.score.parameters.datasource,
            }),
            ...(this.dataProxy.rawIndexes
              ? [
                  this.editMultipleDataSourceFeatures({
                    ...this.getMultipleFeaturesEditPayload('indexes', scoresData.indexes),
                    data_source_name: this.currentMetadata.score.indexes.datasource,
                  }),
                ]
              : []),
            this.editNonTopoFeatureAction({
              datasource: this.currentMetadata.monitoringYears.datasource,
              id: this.item.id,
              body: scoresData.main.properties,
            }),
          ]);
          this.$emit('retrieveItem', () => {
            this.$emit('retrieveScores', 'scoreParameters', newDataParams => {
              if (!this.dataProxy.rawIndexes) {
                this.data = JSON.parse(JSON.stringify(newDataParams));
                this.isAddScoreDialogLoaded = this.isAddScoreDialogVisible = false;
                this.isDialogLoading = false;
                return;
              }
              this.$emit('retrieveScores', 'scoreIndexes', newDataIndexes => {
                this.data = JSON.parse(JSON.stringify(newDataIndexes));
                this.isAddScoreDialogLoaded = this.isAddScoreDialogVisible = false;
                this.isDialogLoading = false;
              });
            });
          });
        } else {
          const [r] = await Promise.all([
            this.editMultipleDataSourceFeatures({
              ...addEditParametersPayload,
              data_source_name: this.currentMetadata.score.parameters.datasource,
            }),
            this.editNonTopoFeatureAction({
              datasource: this.currentMetadata.protectedSites.datasource,
              id: this.item.id,
              body: scoresData.main.properties,
            }),
          ]);
          this.$emit('retrieveItem', () => {
            this.$emit('retrieveScores', 'scoreParameters', async newDataParams => {
              if (this.dataProxy.rawIndexes) {
                const addEditIndexesPayload = this.getMultipleFeaturesEditPayload(
                  'indexes',
                  scoresData.indexes,
                  r.data.data.insert
                );
                await this.editMultipleDataSourceFeatures({
                  ...addEditIndexesPayload,
                  data_source_name: this.currentMetadata.score.indexes.datasource,
                });
                this.$emit('retrieveScores', 'scoreIndexes', newDataIndexes => {
                  this.data = JSON.parse(JSON.stringify(newDataIndexes));
                  this.isAddScoreDialogLoaded = this.isAddScoreDialogVisible = false;
                  this.isDialogLoading = false;
                });
              } else {
                this.data = JSON.parse(JSON.stringify(newDataParams));
                this.isAddScoreDialogLoaded = this.isAddScoreDialogVisible = false;
                this.isDialogLoading = false;
              }
            });
          });
        }
      } catch (error) {
        console.log('error', error);
        this.isDialogLoading = false;
      }
    },
    openAddScoreDialog() {
      this.$refs['scores-dialog']?.resetValidation();
      this.isAddScoreDialogLoaded = this.isAddScoreDialogVisible = true;
    },
  },
  beforeMount() {
    this.data = JSON.parse(JSON.stringify(this.dataProxy));
  },
};
</script>
